import React, { useEffect, useState } from "react";
import { CompanyAPI, ProjectAPI } from "../services/api";
import { useNavigate, useLocation } from "react-router-dom";

const CompanyAddUser = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // =========================================================
  // SELECTED COMPANY
  // =========================================================
  const selectedCompanyNo = Number(location.state?.companyNo);

  const [companyUsers, setCompanyUsers] = useState<any[]>([]);
  const [projects, setProjects] = useState<any[]>([]);

  const [selectedEmail, setSelectedEmail] = useState("");
  const [selectedRole, setSelectedRole] = useState("user");

  // =========================================================
  // UPDATE USER ROLE
  // =========================================================
  const [updateEmail, setUpdateEmail] = useState("");
  const [updateRole, setUpdateRole] = useState("user");

  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // =========================================================
  // PROJECT USER MANAGEMENT
  // =========================================================
  const [selectedProject, setSelectedProject] = useState<number | null>(
    null
  );

  const [projectUsers, setProjectUsers] = useState<any[]>([]);

  const [assignProject, setAssignProject] = useState<number | null>(null);
  const [assignUserEmail, setAssignUserEmail] = useState("");

  // =========================================================
  // AVAILABLE USERS
  // =========================================================
  const [availableUsers, setAvailableUsers] = useState<string[]>([]);

  // =========================================================
  // LOAD DATA
  // =========================================================
  const loadData = async () => {
    if (!selectedCompanyNo) {
      setError("Company not selected");
      return;
    }

    // ---------------------------------------------------------
    // COMPANY USERS
    // ---------------------------------------------------------
    try {
      const usersRes = await CompanyAPI.getUsers(selectedCompanyNo);
      setCompanyUsers(usersRes.data || []);
    } catch (err) {
      console.error("Failed to load company users:", err);
      setError("Failed to load company users");
    }

    // ---------------------------------------------------------
    // PROJECTS
    // ---------------------------------------------------------
  try {
  const projectsRes = await ProjectAPI.getAll();

  const companyProjects = (projectsRes || []).filter(
    (project: any) =>
      Number(project.companyNo) === selectedCompanyNo
  );

  setProjects(companyProjects);
} catch (err) {
  console.error("Failed to load projects:", err);
  setError("Failed to load projects");
}
    // ---------------------------------------------------------
    // AVAILABLE USERS
    // ---------------------------------------------------------
    try {
      const availableRes =
  await CompanyAPI.getAvailableUsers(selectedCompanyNo);

const rawUsers = availableRes.data || [];

const emails = rawUsers
  .map((u: any) =>
    typeof u === "string"
      ? u
      : u.email
  )
  .filter(
    (email: any): email is string =>
      typeof email === "string" && email.trim() !== ""
  );

setAvailableUsers(emails);
    } catch (err) {
      console.error("Failed to load available users:", err);
      setError("Failed to load available users");
    }
  };

  // =========================================================
  // INITIAL LOAD
  // =========================================================
  useEffect(() => {
    if (selectedCompanyNo) {
      loadData();
    }
  }, [selectedCompanyNo]);

  // =========================================================
  // AUTO CLEAR MESSAGE
  // =========================================================
  useEffect(() => {
    if (message || error) {
      const timer = setTimeout(() => {
        setMessage(null);
        setError(null);
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [message, error]);

  // =========================================================
  // ADD USER TO COMPANY
  // =========================================================
  const handleAddUser = async () => {
    if (!selectedCompanyNo) {
      setError("Company not selected");
      return;
    }

    if (!selectedEmail) {
      setError("Please select a user");
      return;
    }

    try {
      const res: any = await CompanyAPI.addUser({
        companyNo: selectedCompanyNo,
        email: selectedEmail,
        role: selectedRole,
      });

      setMessage(res.data?.message || "User added to company");

      setSelectedEmail("");

      await loadData();
    } catch (err: any) {
      console.error("Failed to add user:", err);

      setError(
        err.response?.data?.message || "Failed to add user"
      );
    }
  };

  // =========================================================
  // LOAD USER ROLE
  // =========================================================
  const loadUserRole = async (email: string) => {
    if (!email || !selectedCompanyNo) {
      return;
    }

    try {
      const res: any = await CompanyAPI.getUserRole(
        email,
        selectedCompanyNo
      );

      setUpdateRole(
        res.data?.role ??
          res.data?.userType ??
          res.role ??
          "user"
      );
    } catch (err) {
      console.error("Unable to load role:", err);
      setError("Unable to load role");
    }
  };

  // =========================================================
  // UPDATE USER ROLE
  // =========================================================
  const handleUpdateRole = async () => {
    if (!selectedCompanyNo) {
      setError("Company not selected");
      return;
    }

    if (!updateEmail) {
      setError("Select a user");
      return;
    }

    const currentUser = companyUsers.find(
      (u) => u.email === updateEmail
    );

    if (
      currentUser &&
      currentUser.userType?.toLowerCase() ===
        updateRole.toLowerCase()
    ) {
      setMessage(
        `User already has '${updateRole}' role.`
      );
      return;
    }

    try {
      const res: any = await CompanyAPI.updateUserRole({
        companyNo: selectedCompanyNo,
        email: updateEmail,
        role: updateRole,
      });

      setMessage(
        res.data?.message ||
          "Role updated successfully"
      );

      await loadData();

      setUpdateEmail("");
      setUpdateRole("user");
    } catch (err: any) {
      console.error("Failed to update role:", err);

      setError(
        err.response?.data?.message ||
          "Failed to update role"
      );
    }
  };

  // =========================================================
  // REMOVE USER FROM COMPANY
  // =========================================================
  const handleRemoveUser = async (email: string) => {
    if (!selectedCompanyNo) {
      setError("Company not selected");
      return;
    }

    try {
      const res: any = await CompanyAPI.removeUser({
        companyNo: selectedCompanyNo,
        email,
      });

      setMessage(
        res.data?.message ||
          "User removed from company"
      );

      await loadData();
    } catch (err: any) {
      console.error("Failed to remove user:", err);

      setError(
        err.response?.data?.message ||
          "Failed to remove user"
      );
    }
  };

  // =========================================================
  // SEE PROJECT USERS
  // =========================================================
  const handleSeeUsers = async (projectNo: number) => {
    if (selectedProject === projectNo) {
      setSelectedProject(null);
      return;
    }

    setAssignProject(null);
    setSelectedProject(projectNo);

    try {
      const users = await ProjectAPI.getUsers(projectNo);

      setProjectUsers(users);
    } catch (err) {
      console.error(
        "Failed to load project users:",
        err
      );

      setError("Failed to load users");
    }
  };

  // =========================================================
  // REMOVE USER FROM PROJECT
  // =========================================================
  const handleRemoveFromProject = async (
    projectNo: number,
    email: string
  ) => {
    try {
      const res: any =
        await CompanyAPI.removeUserFromProject({
          projectNo,
          email,
        });

      setMessage(
        res.data?.message ||
          "User removed from project"
      );

      // Refresh project users
      const users = await ProjectAPI.getUsers(
        projectNo
      );

      setProjectUsers(users);
    } catch (err: any) {
      console.error(
        "Failed to remove user from project:",
        err
      );

      setError(
        err.response?.data?.message ||
          "Failed to remove user from project"
      );
    }
  };

  // =========================================================
  // ASSIGN USER TO PROJECT
  // =========================================================
  const handleAssignUser = async (
    projectNo: number
  ) => {
    if (!assignUserEmail) {
      setError("Select a user");
      return;
    }

    try {
      const res: any =
        await CompanyAPI.assignUserToProject({
          projectNo,
          email: assignUserEmail,
        });

      setMessage(
        res.data?.message ||
          "User assigned to project"
      );

      setAssignUserEmail("");
      setAssignProject(null);

      // Refresh project users
      const users = await ProjectAPI.getUsers(
        projectNo
      );

      setProjectUsers(users);
      setSelectedProject(projectNo);
    } catch (err: any) {
      console.error(
        "Failed to assign user:",
        err
      );

      setError(
        err.response?.data?.message ||
          "Failed to assign user"
      );
    }
  };

  // =========================================================
  // COMPANY NOT SELECTED
  // =========================================================
  if (!selectedCompanyNo) {
    return (
      <div className="p-6 bg-gray-100 min-h-screen">
        <div className="bg-white p-6 rounded shadow max-w-xl mx-auto">
          <h2 className="text-xl font-semibold text-red-600 mb-3">
            Company not selected
          </h2>

          <p className="text-gray-600 mb-4">
            Please select a company from the Super Admin
            page.
          </p>

          <button
            onClick={() =>
              navigate("/super-admin")
            }
            className="bg-blue-600 text-white px-4 py-2 rounded"
          >
            Select Company
          </button>
        </div>
      </div>
    );
  }

  // =========================================================
  // UI
  // =========================================================
  return (
    <div className="p-6 bg-gray-100 min-h-screen">

      {/* =====================================================
          HEADER
      ===================================================== */}
      <div className="flex justify-between items-center mb-6">

        <div>
          <h1 className="text-2xl font-bold">
            Company Dashboard
          </h1>

          <p className="text-sm text-gray-500 mt-1">
            Company No: {selectedCompanyNo}
          </p>
        </div>

        <button
          onClick={() =>
            navigate("/super-admin")
          }
          className="bg-gray-700 text-white px-4 py-2 rounded"
        >
          Back to Companies
        </button>

      </div>

      {/* =====================================================
          COMPANY USERS
      ===================================================== */}
      <div className="bg-white p-4 rounded shadow mb-6">

        <h2 className="font-semibold mb-3">
          Company Users
        </h2>

        {companyUsers.length === 0 ? (
          <p className="text-gray-500 text-sm">
            No users found.
          </p>
        ) : (
          companyUsers.map((u, i) => (
            <div
              key={i}
              className="flex justify-between items-center border p-3 mb-2 rounded"
            >
              <div>
                <span className="font-medium">
                  {u.email}
                </span>

                {u.userType && (
                  <span className="ml-3 text-xs bg-gray-100 px-2 py-1 rounded">
                    {u.userType}
                  </span>
                )}
              </div>

              <button
                onClick={() =>
                  handleRemoveUser(u.email)
                }
                className="text-red-500 text-sm"
              >
                Remove
              </button>
            </div>
          ))
        )}

      </div>

      {/* =====================================================
          ADD USER
      ===================================================== */}
      <div className="bg-white p-4 rounded shadow mb-6">

        <h2 className="font-semibold mb-3">
          Add User
        </h2>

        {/* USER EMAIL */}
        <div className="mb-3">

          <input
            value={selectedEmail}
            onChange={(e) =>
              setSelectedEmail(e.target.value)
            }
            placeholder="Enter email"
            className="border p-2 w-full rounded"
          />

          {selectedEmail && (
            <div className="mt-1 w-full bg-white border rounded shadow max-h-40 overflow-y-auto">

              {availableUsers
                .filter(
                  (email) =>
                    email
                      .toLowerCase()
                      .includes(
                        selectedEmail.toLowerCase()
                      )
                )
                .map((email, i) => (
                  <div
                    key={i}
                    onClick={() =>
                      setSelectedEmail(email)
                    }
                    className="p-2 hover:bg-blue-100 cursor-pointer text-sm"
                  >
                    {email}
                  </div>
                ))}

              {!availableUsers.some(
                (email) =>
                  email.toLowerCase() ===
                  selectedEmail.toLowerCase()
              ) && (
                <div className="p-2 text-red-400 text-sm">
                  User not available or already assigned
                </div>
              )}

            </div>
          )}

        </div>

        {/* ROLE */}
        <select
          value={selectedRole}
          onChange={(e) =>
            setSelectedRole(e.target.value)
          }
          className="border p-2 w-full mb-3 rounded"
        >
          <option value="superadmin">
            Super Admin
          </option>

          <option value="admin">
            Admin
          </option>

          <option value="user">
            Normal User
          </option>

          <option value="client">
            Client
          </option>

          <option value="external">
            External
          </option>
        </select>

        {/* ADD */}
        <button
          onClick={handleAddUser}
          className="bg-green-600 text-white px-4 py-2 rounded w-full"
        >
          Add User
        </button>

      </div>

      {/* =====================================================
          UPDATE USER ROLE
      ===================================================== */}
      <div className="bg-white p-4 rounded shadow mb-6">

        <h2 className="font-semibold mb-3">
          Update User Role
        </h2>

        {/* SELECT USER */}
        <select
          value={updateEmail}
          onChange={async (e) => {
            const email = e.target.value;

            setUpdateEmail(email);

            if (email) {
              await loadUserRole(email);
            }
          }}
          className="border p-2 w-full mb-3 rounded"
        >
          <option value="">
            Select User
          </option>

          {companyUsers.map((u, i) => (
            <option
              key={i}
              value={u.email}
            >
              {u.email}
            </option>
          ))}
        </select>

        {/* SELECT ROLE */}
        <select
          value={updateRole}
          onChange={(e) =>
            setUpdateRole(e.target.value)
          }
          className="border p-2 w-full mb-3 rounded"
        >
          <option value="superadmin">
            Super Admin
          </option>

          <option value="admin">
            Admin
          </option>

          <option value="user">
            Normal User
          </option>

          <option value="client">
            Client
          </option>

          <option value="external">
            External
          </option>
        </select>

        {/* UPDATE */}
        <button
          onClick={handleUpdateRole}
          className="bg-blue-600 text-white px-4 py-2 rounded w-full"
        >
          Update Role
        </button>

      </div>

      {/* =====================================================
          PROJECTS
      ===================================================== */}
      <div className="bg-white p-4 rounded shadow">

        {/* HEADER */}
        <div className="flex justify-between items-center mb-3">

          <h2 className="font-semibold">
            Company Projects
          </h2>

          <button
            onClick={() => navigate("/")}
            className="bg-blue-600 text-white px-3 py-1 rounded text-sm"
          >
            Open Projects Page
          </button>

        </div>

        {projects.length === 0 ? (
          <p className="text-gray-500 text-sm">
            No projects found.
          </p>
        ) : (
          projects.map((p) => (

            <div
              key={p.projectNo}
              className="border p-3 mb-3 rounded"
            >

              {/* PROJECT HEADER */}
              <div className="flex justify-between items-center">

                <span className="font-medium">
                  {p.projectName}
                </span>

                <div className="flex gap-4">

                  {/* SEE USERS */}
                  <button
                    onClick={() =>
                      handleSeeUsers(
                        p.projectNo
                      )
                    }
                    className="text-blue-600 underline text-sm"
                  >
                    See Users
                  </button>

                  {/* ASSIGN USER */}
                  <button
                    onClick={async () => {
                      setSelectedProject(null);
                      setAssignProject(
                        p.projectNo
                      );

                      try {
                        const users =
                          await ProjectAPI.getUsers(
                            p.projectNo
                          );

                        setProjectUsers(users);
                      } catch {
                        setError(
                          "Failed to load users"
                        );
                      }
                    }}
                    className="text-purple-600 underline text-sm"
                  >
                    Assign User
                  </button>

                </div>

              </div>

              {/* =================================================
                  USERS
              ================================================= */}
              {selectedProject ===
                p.projectNo && (

                <div className="mt-4 border-t pt-3">

                  <h3 className="font-semibold mb-2">
                    Users
                  </h3>

                  {projectUsers.length === 0 ? (
                    <p className="text-gray-500 text-sm">
                      No users assigned.
                    </p>
                  ) : (
                    projectUsers.map((u, i) => (

                      <div
                        key={i}
                        className="flex justify-between mb-2"
                      >

                        <span>
                          {u.email}
                        </span>

                        <button
                          onClick={() =>
                            handleRemoveFromProject(
                              p.projectNo,
                              u.email
                            )
                          }
                          className="text-red-500 text-sm"
                        >
                          Remove
                        </button>

                      </div>

                    ))
                  )}

                </div>

              )}

              {/* =================================================
                  ASSIGN USER
              ================================================= */}
              {assignProject ===
                p.projectNo && (

                <div className="mt-4 border-t pt-3">

                  <select
                    value={assignUserEmail}
                    onChange={(e) =>
                      setAssignUserEmail(
                        e.target.value
                      )
                    }
                    className="w-full border p-2 rounded mb-2"
                  >

                    <option value="">
                      Select User
                    </option>

                    {companyUsers
                      .filter(
                        (u) =>
                          u.userType
                            ?.toLowerCase() ===
                          "user"
                      )
                      .filter(
                        (u) =>
                          !projectUsers.some(
                            (pu) =>
                              pu.email ===
                              u.email
                          )
                      )
                      .map((u, i) => (

                        <option
                          key={i}
                          value={u.email}
                        >
                          {u.email}
                        </option>

                      ))}

                  </select>

                  <button
                    onClick={() =>
                      handleAssignUser(
                        p.projectNo
                      )
                    }
                    className="w-full bg-purple-600 text-white py-2 rounded"
                  >
                    Assign
                  </button>

                </div>

              )}

            </div>

          ))
        )}

      </div>

      {/* =====================================================
          SUCCESS MESSAGE
      ===================================================== */}
      {message && (
        <div className="fixed top-4 right-4 bg-green-600 text-white px-4 py-2 rounded shadow z-50">
          {message}
        </div>
      )}

      {/* =====================================================
          ERROR MESSAGE
      ===================================================== */}
      {error && (
        <div className="fixed top-4 right-4 bg-red-500 text-white px-4 py-2 rounded shadow z-50">
          {error}
        </div>
      )}

    </div>
  );
};

export default CompanyAddUser;